import React from 'react';
import { OrganizationStatus, State } from 'lib/enums';
import { EOrganization, ESnapshotExists, exists } from 'lib/types';
import { Autocomplete } from 'lib/components/Autocomplete';
import { SearchableOrganizationRecord } from 'lib/types/searchable';

type SelectPublisherDropdownProps = {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  onPublisherOrganizationChange: (publisherOrganizationId: string) => void;
  publisherOptions: SearchableOrganizationRecord[];
  loading: boolean;
};

export function SelectPublisherDropdown({
  publisherOrganization,
  onPublisherOrganizationChange,
  publisherOptions,
  loading
}: SelectPublisherDropdownProps) {
  const formattedPublisherOptions = publisherOptions.map(formatOptions);

  const noteText = getPublisherLocationNoteText(publisherOrganization?.data());

  return (
    <Autocomplete
      id="selectPublisher"
      labelText="Publisher"
      noteText={!loading ? noteText : ''}
      placeholder="Select a publisher"
      value={
        exists(publisherOrganization) && !loading
          ? publisherOrganization.id
          : ''
      }
      options={formattedPublisherOptions}
      onChange={value => onPublisherOrganizationChange(value)}
      loading={loading}
    />
  );
}

export function formatOptions(paper: SearchableOrganizationRecord) {
  // TODO(kevinm): Move this derived label into a model helper
  return {
    value: paper.id,
    label: paper.name,
    isExternalPublisher: paper.status === OrganizationStatus.express.value,
    description: getPublisherLocationDescription(paper)
  };
}

export function getPublisherLocationDescription({
  state,
  county,
  city
}: Pick<EOrganization, 'state' | 'county' | 'city'>) {
  const stateItem = State.by_value(state);
  const isLouisiana = stateItem?.key === 'louisiana';
  const countyLabel = isLouisiana ? 'Parish' : 'County';
  const countyDescription = county
    .toLowerCase()
    .includes(countyLabel.toLowerCase())
    ? county
    : `${county} ${countyLabel}`;

  return [city, countyDescription, stateItem?.abbrev.toUpperCase()]
    .filter(Boolean)
    .join(', ');
}

function getPublisherLocationNoteText(
  publisherOrganization: EOrganization | undefined
) {
  const { state: stateId } = publisherOrganization || {};

  const stateLabel = State.by_value(stateId)?.label;
  const noteText = stateLabel
    ? `This publisher is located in ${stateLabel}.`
    : undefined;

  return noteText;
}
